import { useCallback, useContext, useEffect, useRef } from "react";

import { PAGE_GROUP_PREVIEW_MESSAGE_TYPE } from "../../shared/types/page-group-preview";

import { PubsubWebsocketContext } from "providers/PubsubWebsocketProvider";
import { useAppSelector } from "store/index";
import { selectCurrentUserPreviewGroupId } from "store/selectors";

export const useSubscribePageGroupPreview = (
  pageId: string,
  { clearOnUnmount = false }: { clearOnUnmount?: boolean } = {},
) => {
  const { sendPubsubMessage, pubsubStatus } = useContext(PubsubWebsocketContext);
  const sendPubsubMessageRef = useRef(sendPubsubMessage);

  useEffect(() => {
    sendPubsubMessageRef.current = sendPubsubMessage;
  });

  useEffect(() => {
    if (pubsubStatus !== "connected" || !pageId) {
      return;
    }

    sendPubsubMessage({
      type: PAGE_GROUP_PREVIEW_MESSAGE_TYPE,
      action: "subscribe",
      pageId,
    });

    // sendPubsubMessage is a stable context callback
  }, [pageId, pubsubStatus, sendPubsubMessage]);

  // Connection changes resubscribe above. Clear only when this page's subscription goes away,
  // so a reconnect does not drop the preview for other clients.
  useEffect(() => {
    if (!clearOnUnmount || !pageId) {
      return;
    }

    return () => {
      sendPubsubMessageRef.current({
        type: PAGE_GROUP_PREVIEW_MESSAGE_TYPE,
        action: "clear",
        pageId,
      });
    };
  }, [pageId, clearOnUnmount]);
};

export const usePageGroupPreview = (pageId: string) => {
  const { sendPubsubMessage, pubsubStatus } = useContext(PubsubWebsocketContext);
  const currentUserPreviewGroupId = useAppSelector((state) =>
    selectCurrentUserPreviewGroupId(state, pageId),
  );
  // Keep track
  const currentPreviewGroupIdRef = useRef<string | null>(null);

  useSubscribePageGroupPreview(pageId, { clearOnUnmount: true });

  const setPreview = useCallback(
    (pageGroupId: string) => {
      currentPreviewGroupIdRef.current = pageGroupId;
      sendPubsubMessage({
        type: PAGE_GROUP_PREVIEW_MESSAGE_TYPE,
        action: "set",
        pageId,
        pageGroupId,
      });
    },
    [pageId, sendPubsubMessage],
  );

  const clearPreview = useCallback(() => {
    currentPreviewGroupIdRef.current = null;
    sendPubsubMessage({
      type: PAGE_GROUP_PREVIEW_MESSAGE_TYPE,
      action: "clear",
      pageId,
    });
  }, [pageId, sendPubsubMessage]);

  useEffect(() => {
    currentPreviewGroupIdRef.current = null;
  }, [pageId]);

  // The server drops a preview when its socket disconnects. If the student still has a
  // group selected and the snapshot no longer includes them, send that group again.
  useEffect(() => {
    if (
      pubsubStatus !== "connected" ||
      !currentPreviewGroupIdRef.current ||
      currentUserPreviewGroupId === currentPreviewGroupIdRef.current
    ) {
      return;
    }

    sendPubsubMessage({
      type: PAGE_GROUP_PREVIEW_MESSAGE_TYPE,
      action: "set",
      pageId,
      pageGroupId: currentPreviewGroupIdRef.current,
    });

    // sendPubsubMessage is a stable context callback
  }, [currentUserPreviewGroupId, pageId, pubsubStatus, sendPubsubMessage]);

  return {
    currentUserPreviewGroupId,
    setPreview,
    clearPreview,
  };
};
